import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Alert,
    IconButton,
    useTheme,
    alpha,
    Checkbox,
    Autocomplete,
    TextField,
} from '@mui/material';
import {
    Save as SaveIcon,
    Lock as LockIcon,
    ChevronRight as ChevronRightIcon,
    ExpandMore as ExpandMoreIcon,
    CheckBox as CheckBoxIcon,
    CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
    IndeterminateCheckBox as IndeterminateCheckBoxIcon,
    UnfoldMore as UnfoldMoreIcon,
    UnfoldLess as UnfoldLessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
    ColDef,
    ModuleRegistry,
    ClientSideRowModelModule,
    ValidationModule,
    QuickFilterModule,
    PaginationModule,
    RowStyleModule,
    RowAutoHeightModule,
    GridReadyEvent,
    GridApi,
    ICellRendererParams,
    ExternalFilterModule,
    RenderApiModule,
} from 'ag-grid-community';

import { useAuth } from '../contexts/AuthContext';
import { hasCapability, CAP } from '../utils/permissions';
import projectsService from '../services/projectsService';
import { Project, User, WorkPackage, WorkUnit } from '../types';

import LoadingAnimation from '../components/LoadingAnimation';
import { getZoomCompensatedAutocompleteSlotProps } from '../utils/zoomCompensation';

// Register AG Grid modules
ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    ValidationModule,
    QuickFilterModule,
    PaginationModule,
    RowStyleModule,
    RowAutoHeightModule,
    ExternalFilterModule,
    RenderApiModule
]);

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

// Row Data Interface
interface PermissionRow {
    id: string; // Unique ID for AG Grid
    type: 'WP' | 'WU';
    wpId: number;
    wuId: number | null;
    wpCode: string;
    wuCode: string;
    wpTitle: string;
    wuTitle: string;
    // Dynamic user fields will be accessed via params.data[userId]
    [key: string]: any;
}

const PermissionsManagementPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Nautilus Console Design System Tokens
    const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b', abyssal: '#0a1622' };
    const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
    const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';

    const isPMOrAdmin = hasCapability(user, CAP.MANAGE_WP_PERMISSIONS);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProjectId, setSelectedProjectId] = useState<number | ''>('');

    const [users, setUsers] = useState<User[]>([]);
    const [workPackages, setWorkPackages] = useState<WorkPackage[]>([]);
    const [workUnits, setWorkUnits] = useState<WorkUnit[]>([]);

    // Track assignments: user_id -> work_package_ids[]
    const [workPackageAssignments, setWorkPackageAssignments] = useState<{ [userId: number]: number[] }>({});
    // Track assignments: user_id -> work_unit_ids[]
    const [workUnitAssignments, setWorkUnitAssignments] = useState<{ [userId: number]: number[] }>({});

    // Track changes
    const [hasChanges, setHasChanges] = useState(false);

    // Expanded Work Packages State
    const [expandedWPIds, setExpandedWPIds] = useState<number[]>([]);

    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    // Load projects on mount
    useEffect(() => {
        if (!isPMOrAdmin) {
            navigate('/dashboard');
            return;
        }
        loadProjects();
    }, [isPMOrAdmin, navigate]);

    // Load permissions when project is selected
    useEffect(() => {
        if (selectedProjectId) {
            loadPermissions();
        }
    }, [selectedProjectId]);

    const loadProjects = async () => {
        try {
            setLoading(true);
            const projectsData = await projectsService.getProjects();
            setProjects(projectsData);
        } catch (err) {
            setError('Failed to load projects');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadPermissions = async () => {
        if (!selectedProjectId) return;

        try {
            setLoading(true);
            setError(null);
            const data = await projectsService.getPermissions(selectedProjectId as number);

            setUsers(data.users);
            setWorkPackages(data.work_packages);
            setWorkUnits(data.work_units);
            setWorkPackageAssignments(data.assignments.work_packages);
            setWorkUnitAssignments(data.assignments.work_units);
            setHasChanges(false);
            setExpandedWPIds([]); // Reset expansion
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to load permissions');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSavePermissions = async () => {
        if (!selectedProjectId) return;

        try {
            setSaving(true);
            setError(null);
            setSuccess(null);

            await projectsService.updatePermissions(selectedProjectId as number, {
                work_packages: workPackageAssignments,
                work_units: workUnitAssignments,
            });

            setSuccess('Permissions saved successfully!');
            setHasChanges(false);

            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to save permissions');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    // --- Logic for Assignments ---

    const isWPAssigned = useCallback((userId: number, wpId: number) => {
        return (workPackageAssignments[userId] || []).includes(wpId);
    }, [workPackageAssignments]);

    const isWUAssigned = useCallback((userId: number, wuId: number) => {
        return (workUnitAssignments[userId] || []).includes(wuId);
    }, [workUnitAssignments]);

    const getWPAssignedStatus = useCallback((userId: number, wpId: number) => {
        // Get all WUs for this WP
        const wpUnits = workUnits.filter(wu => wu.work_package_id === wpId);

        if (wpUnits.length === 0) {
            // If no WUs, rely on WP assignment directly
            return isWPAssigned(userId, wpId) ? 'full' : 'none';
        }

        // Check how many WUs are assigned
        const assignedCount = wpUnits.filter(wu => isWUAssigned(userId, wu.id)).length;

        if (assignedCount === 0) {
            // Even if WP is assigned in DB, if no WUs are assigned, we show 'none' 
            return 'none';
        } else if (assignedCount === wpUnits.length) {
            return 'full';
        } else {
            return 'partial';
        }
    }, [workUnits, isWPAssigned, isWUAssigned]);

    const toggleWP = useCallback((userId: number, wpId: number) => {
        const currentStatus = getWPAssignedStatus(userId, wpId);
        const wpUnits = workUnits.filter(wu => wu.work_package_id === wpId);
        const wpUnitIds = wpUnits.map(wu => wu.id);

        let newWPAssignments = { ...workPackageAssignments };
        let newWUAssignments = { ...workUnitAssignments };
        const currentUserWPs = newWPAssignments[userId] || [];
        const currentUserWUs = newWUAssignments[userId] || [];

        if (currentStatus === 'none' || currentStatus === 'partial') {
            // Switch to Full: Assign WP and ALL WUs
            if (!currentUserWPs.includes(wpId)) {
                newWPAssignments[userId] = [...currentUserWPs, wpId];
            }
            // Add all WUs that are not already present
            const missingWUs = wpUnitIds.filter(id => !currentUserWUs.includes(id));
            if (missingWUs.length > 0) {
                newWUAssignments[userId] = [...currentUserWUs, ...missingWUs];
            }
        } else {
            // Switch to None: Unassign WP and ALL WUs
            newWPAssignments[userId] = currentUserWPs.filter(id => id !== wpId);
            newWUAssignments[userId] = currentUserWUs.filter(id => !wpUnitIds.includes(id));
        }

        setWorkPackageAssignments(newWPAssignments);
        setWorkUnitAssignments(newWUAssignments);
        setHasChanges(true);
        // Refresh grid
        gridApi?.refreshCells();
    }, [workPackageAssignments, workUnitAssignments, workUnits, getWPAssignedStatus, gridApi]);

    const toggleWU = useCallback((userId: number, wuId: number, wpId: number) => {
        const isAssigned = isWUAssigned(userId, wuId);

        let newWUAssignments = { ...workUnitAssignments };
        let newWPAssignments = { ...workPackageAssignments };
        const currentUserWUs = newWUAssignments[userId] || [];
        const currentUserWPs = newWPAssignments[userId] || [];

        if (isAssigned) {
            // Unassign WU
            newWUAssignments[userId] = currentUserWUs.filter(id => id !== wuId);
            // We do NOT automatically unassign WP here, as per "Partial Access" logic.
        } else {
            // Assign WU
            newWUAssignments[userId] = [...currentUserWUs, wuId];
            // Ensure WP is assigned
            if (!currentUserWPs.includes(wpId)) {
                newWPAssignments[userId] = [...currentUserWPs, wpId];
            }
        }

        setWorkUnitAssignments(newWUAssignments);
        setWorkPackageAssignments(newWPAssignments);
        setHasChanges(true);
        gridApi?.refreshCells();
    }, [workUnitAssignments, workPackageAssignments, isWUAssigned, gridApi]);


    // --- Grid Data Preparation ---

    const rowData = useMemo(() => {
        const rows: PermissionRow[] = [];

        // Sort WPs by code
        const sortedWPs = [...workPackages].sort((a, b) => (a.code || '').localeCompare(b.code || ''));

        sortedWPs.forEach(wp => {
            // WP Row
            rows.push({
                id: `wp-${wp.id}`,
                type: 'WP',
                wpId: wp.id,
                wuId: null,
                wpCode: wp.code || '',
                wuCode: '',
                wpTitle: wp.title || '',
                wuTitle: '',
            });

            // WU Rows
            const wpUnits = workUnits.filter(wu => wu.work_package_id === wp.id)
                .sort((a, b) => (a.code || '').localeCompare(b.code || ''));

            wpUnits.forEach(wu => {
                rows.push({
                    id: `wu-${wu.id}`,
                    type: 'WU',
                    wpId: wp.id,
                    wuId: wu.id,
                    wpCode: wp.code || '', // Keep WP code for grouping/sorting if needed
                    wuCode: wu.code || '',
                    wpTitle: wp.title || '',
                    wuTitle: wu.title || '',
                });
            });
        });

        return rows;
    }, [workPackages, workUnits]);

    // --- Grid Configuration ---

    const toggleExpand = (wpId: number) => {
        setExpandedWPIds(prev =>
            prev.includes(wpId) ? prev.filter(id => id !== wpId) : [...prev, wpId]
        );
        // We need to trigger a filter change
        setTimeout(() => gridApi?.onFilterChanged(), 0);
    };

    const expandAll = () => {
        const expandableWPIds = workPackages.filter(wp => workUnits.some(wu => wu.work_package_id === wp.id)).map(wp => wp.id);
        setExpandedWPIds(expandableWPIds);
    };

    const collapseAll = () => {
        setExpandedWPIds([]);
    };

    const isExternalFilterPresent = useCallback(() => {
        return true; // Always active to handle expansion
    }, []);

    const doesExternalFilterPass = useCallback((node: any) => {
        const data = node.data as PermissionRow;
        if (data.type === 'WP') return true; // Always show WP rows
        if (data.type === 'WU') {
            return expandedWPIds.includes(data.wpId);
        }
        return true;
    }, [expandedWPIds]);

    // Custom Renderers

    const WPCellRenderer = (params: ICellRendererParams) => {
        const { data } = params;
        if (data.type === 'WP') {
            const isExpanded = expandedWPIds.includes(data.wpId);
            const hasChildren = workUnits.some(wu => wu.work_package_id === data.wpId);

            return (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    {hasChildren && (
                        <IconButton
                            size="small"
                            onClick={() => toggleExpand(data.wpId)}
                            sx={{ p: 0.5, mr: 1 }}
                        >
                            {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                        </IconButton>
                    )}
                    <Typography variant="body2" fontWeight="bold">
                        {data.wpCode} {data.wpTitle ? `- ${data.wpTitle}` : ''}
                    </Typography>
                </Box>
            );
        } else {
            // For WU rows, show nothing in WP column
            return null;
        }
    };

    const UserCellRenderer = (params: ICellRendererParams & { userId: number }) => {
        const { data, userId } = params;

        if (data.type === 'WP') {
            const status = getWPAssignedStatus(userId, data.wpId);

            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <IconButton
                        size="small"
                        onClick={() => toggleWP(userId, data.wpId)}
                        color={status !== 'none' ? 'primary' : 'default'}
                    >
                        {status === 'full' && <CheckBoxIcon fontSize="small" />}
                        {status === 'partial' && <IndeterminateCheckBoxIcon fontSize="small" />}
                        {status === 'none' && <CheckBoxOutlineBlankIcon fontSize="small" />}
                    </IconButton>
                </Box>
            );
        } else {
            const isAssigned = isWUAssigned(userId, data.wuId!);

            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Checkbox
                        size="small"
                        checked={isAssigned}
                        onChange={() => toggleWU(userId, data.wuId!, data.wpId)}
                        color="primary"
                    />
                </Box>
            );
        }
    };

    const columnDefs = useMemo<ColDef[]>(() => {
        const cols: ColDef[] = [
            {
                headerName: 'Work Package',
                field: 'wpCode',
                width: 300,
                pinned: 'left',
                cellRenderer: WPCellRenderer,
                filter: true,
            },
            {
                headerName: 'Work Unit',
                field: 'wuCode',
                width: 250,
                pinned: 'left',
                cellRenderer: (params: ICellRendererParams) => {
                    if (params.data.type === 'WU') {
                        return (
                            <Box sx={{ pl: 2 }}>
                                {params.value} {params.data.wuTitle ? `- ${params.data.wuTitle}` : ''}
                            </Box>
                        );
                    }
                    return null;
                },
                filter: true,
            }
        ];

        // Add User Columns
        users.forEach(user => {
            cols.push({
                headerName: user.name,
                field: `user_${user.id}`, // Virtual field
                minWidth: 100,
                sortable: false, // Disable sorting to preserve hierarchy
                filter: false,
                cellRenderer: (params: ICellRendererParams) => <UserCellRenderer {...params} userId={user.id} />,
                headerComponent: () => (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="bold" noWrap>
                            {user.name}
                        </Typography>
                        {isPMOrAdmin && (
                            <Typography variant="caption" color="primary">
                                PM/Admin
                            </Typography>
                        )}
                    </Box>
                )
            });
        });

        return cols;
    }, [users, expandedWPIds, workUnits, getWPAssignedStatus, isWUAssigned]);

    const defaultColDef = useMemo(() => ({
        resizable: true,
        sortable: false, // Default no sort to keep hierarchy
    }), []);

    const onGridReady = useCallback((params: GridReadyEvent) => {
        setGridApi(params.api);
    }, []);

    // Update filter when expansion changes
    useEffect(() => {
        if (gridApi) {
            gridApi.onFilterChanged();
        }
    }, [expandedWPIds, gridApi]);

    if (!isPMOrAdmin) {
        return null;
    }

    return (
        <Box sx={{ p: 3, height: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Typography
                variant="h4"
                gutterBottom
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    color: isDark ? '#ffffff' : NAUTILUS.deepOcean,
                }}
            >
                <LockIcon sx={{ color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }} /> Work Package/Unit Access
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, color: isDark ? '#b0bec5' : '#546e7a' }}>
                Manage user access to work packages and work units allowing them to log time to their timesheets.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            <Paper sx={{ p: 2, mb: 2, border: `1px solid ${panelBorder}`, boxShadow: 'none', bgcolor: surfaceBg, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Autocomplete
                        sx={{ minWidth: 300 }}
                        size="small"
                        options={projects}
                        getOptionLabel={(project) => project.name}
                        value={projects.find(p => p.id === selectedProjectId) || null}
                        onChange={(_, newValue) => setSelectedProjectId(newValue?.id || '')}
                        slotProps={getZoomCompensatedAutocompleteSlotProps()}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Project"
                            />
                        )}
                    />

                    <Box sx={{ flex: 1 }} />

                    <Button
                        variant="outlined"
                        startIcon={<UnfoldMoreIcon />}
                        onClick={expandAll}
                        disabled={!selectedProjectId}
                        sx={{
                            borderColor: panelBorder,
                            color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                            '&:hover': {
                                borderColor: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                            },
                        }}
                    >
                        Expand All
                    </Button>

                    <Button
                        variant="outlined"
                        startIcon={<UnfoldLessIcon />}
                        onClick={collapseAll}
                        disabled={!selectedProjectId}
                        sx={{
                            borderColor: panelBorder,
                            color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                            '&:hover': {
                                borderColor: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                            },
                        }}
                    >
                        Collapse All
                    </Button>

                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSavePermissions}
                        disabled={!selectedProjectId || !hasChanges || saving}
                        sx={{
                            bgcolor: NAUTILUS.sand,
                            color: NAUTILUS.deepOcean,
                            fontWeight: 600,
                            '&:hover': {
                                bgcolor: alpha(NAUTILUS.sand, 0.85),
                            },
                            '&:disabled': {
                                bgcolor: alpha(NAUTILUS.sand, 0.3),
                            },
                        }}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Box>
            </Paper>

            {selectedProjectId && (
                <Paper
                    sx={{
                        flex: 1,
                        overflow: 'hidden',
                        border: `1px solid ${panelBorder}`,
                        boxShadow: 'none',
                        borderRadius: 1,
                    }}
                    className={isDark ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'}
                >
                    {loading ? (
                        <LoadingAnimation />
                    ) : (
                        <AgGridReact
                            rowData={rowData}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            onGridReady={onGridReady}
                            isExternalFilterPresent={isExternalFilterPresent}
                            doesExternalFilterPass={doesExternalFilterPass}
                            getRowId={(params) => params.data.id}
                            rowHeight={40}
                            headerHeight={60}
                            suppressRowTransform={true} // Performance
                            theme="legacy"
                        />
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default PermissionsManagementPage;
